import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/constants";
import DashboardPanels from "@/components/DashboardPanels";
import { ClipboardList, Wrench, ReceiptText, Wallet, Landmark, AlertTriangle, ShieldCheck, CalendarDays, ChevronDown } from "lucide-react";

const today = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);

function Tile({ label, value, sub, color = "#111827", onClick, testid, icon: Icon }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag data-testid={testid} onClick={onClick} className={`min-w-0 rounded-xl border border-[#E2E5EA] bg-white p-3 text-left shadow-[0_1px_2px_rgba(17,24,39,0.05)] transition-colors sm:p-3.5 ${onClick ? "hover:border-[#20466D]" : ""}`} style={{ borderLeft: `4px solid ${color}` }}>
      <div className="flex items-start gap-1.5 text-[12.5px] font-bold uppercase leading-tight tracking-wide text-[#334155] sm:text-[13px]">{Icon && <Icon size={15} className="mt-px shrink-0" style={{ color }} />}<span className="min-w-0">{label}</span></div>
      <div className="ti-num mt-1 break-words text-[22px] leading-tight sm:text-[26px]" style={{ color }}>{value}</div>
      {sub && <div className="text-[13px] font-medium text-[#475569]">{sub}</div>}
    </Tag>
  );
}

const Sec = ({ title, children }) => (
  <section className="mt-5"><h2 className="mb-2 text-[17px] font-extrabold tracking-wide text-[#111827]">{title}</h2>{children}</section>
);

export default function OwnerDashboard() {
  const [d, setD] = useState(null);
  const [day, setDay] = useState(today());
  const [full, setFull] = useState(false);
  const nav = useNavigate();
  useEffect(() => { setD(null); api.get("/dashboard/owner-brief", { params: { date: day } }).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [day]);
  if (!d) return <p className="py-10 text-center text-[#475569]">Memuat…</p>;
  if (d.error) return <p className="py-10 text-center text-[#B91C1C]">Gagal memuat dashboard.</p>;
  const f = d.finance, a = d.alerts, r = d.daily;
  const isToday = day === today();
  return (
    <div data-testid="owner-dashboard" className="mx-auto max-w-6xl">
      <h1 className="font-heading">OWNER DASHBOARD</h1>
      <p className="mt-0.5 text-[15px] font-medium text-[#475569]">Ringkasan bengkel · {d.total_units} unit aktif</p>

      <Sec title="Unit per Fase">
        <div className="grid grid-cols-3 gap-2.5">
          <Tile testid="ob-pre" icon={ClipboardList} label="Pre Order" value={d.phases.PRE_ORDER} color="#20466D" onClick={() => nav("/pre-order")} />
          <Tile testid="ob-repair" icon={Wrench} label="Repair" value={d.phases.REPAIR_ORDER} color="#B45309" onClick={() => nav("/repair-order")} />
          <Tile testid="ob-finish" icon={ReceiptText} label="Finish" value={d.phases.FINISH_ORDER} color="#15803D" onClick={() => nav("/finish-order")} />
        </div>
      </Sec>

      <Sec title="Keuangan">
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          <Tile testid="ob-omzet" icon={ReceiptText} label="Omzet bulan ini" value={formatRupiah(f.omzet_month)} sub="total invoice bulan berjalan" color="#8A641A" />
          <Tile testid="ob-piutang" icon={Wallet} label="Piutang" value={formatRupiah(f.piutang)} color="#20466D" onClick={() => nav("/finance")} />
          <Tile testid="ob-kas" icon={Wallet} label="Kas hari ini" value={formatRupiah(f.cash)} onClick={() => nav("/cash-bank")} />
          <Tile testid="ob-bank" icon={Landmark} label="Bank hari ini" value={formatRupiah(f.bank)} onClick={() => nav("/cash-bank")} />
        </div>
      </Sec>

      <Sec title="Perlu Perhatian">
        <div className="grid grid-cols-3 gap-2.5">
          <Tile testid="ob-late" icon={AlertTriangle} label="Unit terlambat" value={a.late_units} color={a.late_units ? "#B91C1C" : "#15803D"} onClick={() => nav("/repair-order")} />
          <Tile testid="ob-spk-approval" icon={ShieldCheck} label="Approval SPK" value={a.spk_approval} color={a.spk_approval ? "#B45309" : "#15803D"} onClick={() => nav("/pre-order")} />
          <Tile testid="ob-or-approval" icon={ShieldCheck} label="Approval OR" value={a.or_approval} color={a.or_approval ? "#B45309" : "#15803D"} onClick={() => nav("/or-payments")} />
        </div>
      </Sec>

      <Sec title={`Laporan Harian Admin${isToday ? " · hari ini" : ""}`}>
        <div className="mb-2 flex items-center gap-2 text-[14px] font-semibold text-[#334155]"><CalendarDays size={16} /><input data-testid="ob-date" type="date" value={day} onChange={(e) => setDay(e.target.value || today())} className="h-10 rounded-lg border border-[#CBD2DA] bg-white px-2 text-[15px]" /></div>
        <div data-testid="ob-daily" className="overflow-hidden rounded-xl border border-[#E2E5EA] bg-white">
          {[["WO In", r.wo_in], ["Unit In", r.unit_in], ["Unit Out", r.unit_out], ["Invoice", `${r.invoice_count} · ${formatRupiah(r.invoice_value)}`], ["Piutang", formatRupiah(r.piutang)], ["Hutang", formatRupiah(r.hutang)], ["WO On Progress", r.wo_on_progress]].map(([k, v], i) => (
            <div key={k} data-testid={`ob-daily-${k.toLowerCase().replace(/\s/g, "-")}`} className={`flex items-center justify-between px-4 py-2.5 ${i ? "border-t border-[#E2E5EA]" : ""}`}>
              <span className="text-[15px] font-semibold text-[#334155]">{k}</span><span className="ti-num text-[17px]">{v}</span>
            </div>
          ))}
        </div>
      </Sec>

      <button data-testid="ob-toggle-full" onClick={() => setFull(!full)} className="mt-5 flex items-center gap-1.5 text-[15px] font-bold text-[#20466D] hover:underline"><ChevronDown size={17} className={full ? "rotate-180" : ""} />{full ? "Sembunyikan panel lengkap" : "Tampilkan panel lengkap"}</button>
      {full && <div className="mt-3"><DashboardPanels title="PANEL LENGKAP" subtitle="bulan berjalan" range={{ from: `${day.slice(0, 7)}-01`, to: day }} /></div>}
    </div>
  );
}
